/**
 * The channel overview: how much of each thing exists, and the way into each.
 *
 * Counts only, and no comparison between channels anywhere — there is nowhere
 * in this screen to put a number that would invite one (ADR 0030).
 */
import type { ReactNode } from 'react';
import { Shell } from '../components/Shell.jsx';
import { Link, Failed, Spinner } from '../components/primitives.jsx';
import { hrefFor } from '../lib/router.js';
import { bundles, instances, rewards, triggers } from '../lib/tomo.js';
import { useAsync } from '../useAsync.js';
import { useFeature, useSession } from '../Session.jsx';
import { TwitchPanel } from '../components/TwitchPanel.jsx';

export function ChannelHome({ channelId }: { channelId: string }): ReactNode {
  const session = useSession();
  const channel = session.channels.find((c) => c.id === channelId);
  const collectibles = useFeature('collectibles');

  return (
    <Shell channelId={channelId}>
      <h1 className="text-xl font-semibold tracking-tight">
        {channel?.display_name ?? channelId}
      </h1>
      <p className="mt-1 text-sm text-ink-400">
        {channel ? `twitch.tv/${channel.slug}` : null}
      </p>

      {collectibles ? <Overview channelId={channelId} /> : null}

      {/* Owner-only: the server refuses anyone else, so nobody else is shown it. */}
      {channel?.role === 'owner' ? <TwitchPanel channelId={channelId} /> : null}
    </Shell>
  );
}

/**
 * Counts of the catalog, behind the `collectibles` flag. Its own component so
 * that without the flag nothing asks for the catalog at all.
 */
function Overview({ channelId }: { channelId: string }): ReactNode {
  const [result, retry] = useAsync(async (signal) => {
    // In parallel: three independent reads, and the screen is not useful
    // until all three land anyway.
    const [r, b, t, mine] = await Promise.all([
      rewards.list(channelId, signal),
      bundles.list(channelId, signal),
      triggers.list(channelId, signal),
      instances.mine(channelId, signal),
    ]);
    return {
      rewards: r.length,
      bundles: b.length,
      triggers: t.length,
      enabledTriggers: t.filter((x) => x.enabled).length,
      inactiveRewards: r.filter((x) => !x.active).length,
      mine: mine.length,
      wrapped: mine.filter((i) => i.reveal === 'wrapped').length,
    };
  }, [channelId]);

  return (
    <>
      {result.state === 'loading' ? <Spinner label="Loading the catalog…" /> : null}
      {result.state === 'failed' ? (
        <div className="mt-8">
          <Failed message="Could not load this channel." detail={result.error.message} onRetry={retry} />
        </div>
      ) : null}

      {result.state === 'ready' ? (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card
            href={hrefFor([channelId, 'collection'])}
            label="Your collection"
            count={result.value.mine}
            // The thing worth surfacing from an overview: something is
            // waiting to be opened.
            note={result.value.wrapped > 0
              ? `${result.value.wrapped} unopened`
              : undefined}
          />
          <Card
            href={hrefFor([channelId, 'rewards'])}
            label="Rewards"
            count={result.value.rewards}
            note={result.value.inactiveRewards > 0
              ? `${result.value.inactiveRewards} inactive`
              : undefined}
          />
          <Card
            href={hrefFor([channelId, 'bundles'])}
            label="Bundles"
            count={result.value.bundles}
          />
          <Card
            href={hrefFor([channelId, 'triggers'])}
            label="Triggers"
            count={result.value.triggers}
            // An enabled count rather than a total: a trigger that is off is
            // a trigger that silently never fires, and that is the thing
            // worth noticing from the overview.
            note={result.value.triggers > 0
              ? `${result.value.enabledTriggers} enabled`
              : undefined}
          />
        </div>
      ) : null}
    </>
  );
}

function Card(
  { href, label, count, note }:
  { href: string; label: string; count: number; note?: string | undefined }
): ReactNode {
  return (
    <Link
      href={href}
      className="block rounded-lg border border-ink-800 p-5 hover:border-ink-700"
    >
      <span className="block text-3xl tabular-nums">{count}</span>
      <span className="mt-1 block text-sm text-ink-200">{label}</span>
      {note ? <span className="mt-1 block text-xs text-ink-400">{note}</span> : null}
    </Link>
  );
}
